import { z } from "zod";

import { reportSchemaError } from "@/diagnostics/schema-error.ts";

/**
 * Theme resolution. The app stores a *preference* ("system" by default) and
 * always stamps the *resolved* key onto <html data-theme>. CSS never reads
 * `prefers-color-scheme` itself, so there is exactly one source of truth and no
 * way for the stylesheet and the toggle to disagree.
 */

export type ThemeMode = z.infer<typeof ThemeModeSchema>;
export const ThemeModeSchema = z.enum(["system", "light", "dark"]);

/** Declaration order is the toggle's cycle order — see `nextThemeMode`. */
export const THEME_MODES = ThemeModeSchema.options;

export type ResolvedTheme = "light" | "dark";

/** Shared with the inline bootstrap in index.html — keep the two in sync. */
export const THEME_STORAGE_KEY = "radar.theme";

export function systemTheme(): ResolvedTheme {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  return mode === "system" ? systemTheme() : mode;
}

/** Write the resolved key to the document and remember the preference. */
export function applyTheme(mode: ThemeMode): ResolvedTheme {
  const resolved = resolveTheme(mode);
  const root = document.documentElement;
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
  // Keeps the browser UI (and the mobile address bar) in step with the app.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", resolved === "light" ? "#e9e6df" : "#04070a");
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Private browsing: the theme just won't persist.
  }
  return resolved;
}

export function loadThemeMode(): ThemeMode {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    // Absent is the ordinary first-visit case, not a failure. Present-but-wrong
    // is someone's saved preference being discarded, which gets reported.
    if (raw === null) return "system";
    const parsed = ThemeModeSchema.safeParse(raw);
    if (!parsed.success) {
      reportSchemaError("ThemeMode", parsed.error);
      return "system";
    }
    return parsed.data;
  } catch {
    return "system";
  }
}

/** system → light → dark → system. One button, three states. */
export function nextThemeMode(mode: ThemeMode): ThemeMode {
  return THEME_MODES[(THEME_MODES.indexOf(mode) + 1) % THEME_MODES.length];
}

export const THEME_LABELS: Record<ThemeMode, string> = {
  system: "System theme",
  light: "Light theme",
  dark: "Dark theme",
};
